import { useLazyQuery, useQuery } from '@apollo/client'
import { logAnalyticsEvent } from 'api/Firebase'
import { useEffect, useState } from 'react'

import {
  GET_AVAILABLE_PLANS,
  GET_CHECKOUT_SESSION,
  GET_TENANT_STORES,
  GetAvailablePlansQuery,
  GetAvailablePlansQueryVariables,
  GetCheckoutSessionQuery,
  GetCheckoutSessionQueryVariables,
  GetTenantStoresQuery,
  GetTenantStoresQueryVariables,
  SubscriptionTypeFragment,
} from '@/graphql'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'

const usePricingTable = () => {
  const { t } = useT()

  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [checkoutError, setCheckoutError] = useState<string | null>(null)

  const [fetchCheckoutSession] = useLazyQuery<
    GetCheckoutSessionQuery,
    GetCheckoutSessionQueryVariables
  >(GET_CHECKOUT_SESSION, {
    onCompleted: data => {
      setClientSecret(data.checkoutSession.clientSecret)
    },
  })

  const handleSelectPlan = async (plan: SubscriptionTypeFragment) => {
    try {
      setSelectedPlan(plan.id)
      logAnalyticsEvent('plan_selected', { planId: plan.id })
      const returnUrl = `${window.location.origin}/account-set-up?step=payment-success`

      if (!user?.tenant?.paymentCustomerId) {
        throw new Error('No customer ID found')
      }

      await fetchCheckoutSession({
        variables: {
          input: {
            customerId: user.tenant.paymentCustomerId,
            priceId: plan.prices[0].id,
            returnUrl,
          },
        },
        fetchPolicy: 'network-only',
      })
    } catch (error) {
      setCheckoutError(
        error instanceof Error ? error.message : 'An error occurred',
      )
    }
  }

  const handleCancelCheckout = () => {
    setClientSecret(null)
    setSelectedPlan(null)
  }

  const { user } = useLoggedInUser()

  const {
    data: planData,
    loading: plansLoading,
    error: plansError,
  } = useQuery<GetAvailablePlansQuery, GetAvailablePlansQueryVariables>(
    GET_AVAILABLE_PLANS,
  )
  const [plans, setPlans] = useState<SubscriptionTypeFragment[]>([])

  useEffect(() => {
    if (planData && planData.availablePlans) {
      setPlans(planData.availablePlans)
    }
  }, [planData])

  const [numStores, setNumStores] = useState<number>(0)
  const {
    data: storeData,
    loading: storesLoading,
    error: storesError,
  } = useQuery<GetTenantStoresQuery, GetTenantStoresQueryVariables>(
    GET_TENANT_STORES,
    {
      variables: {
        filters: { isActive: true },
        pagination: { limit: null, offset: 0 },
      },
      fetchPolicy: 'network-only',
    },
  )

  const loading = plansLoading || storesLoading

  const error = plansError || storesError || checkoutError

  useEffect(() => {
    const numActiveStores = storeData?.tenantStores.totalCount
    if (numActiveStores !== undefined) {
      setNumStores(numActiveStores)
    }
  }, [storeData])

  let checkoutInfo = t('pricingTable.currentStoreInfo', { numStores })
  if (numStores === 0) {
    checkoutInfo += ' ' + t('pricingTable.currentStoreExtraInfo')
  }

  return {
    loading,
    error,
    checkoutInfo,
    plans,
    plansLoading,
    plansError,
    selectedPlan,
    clientSecret,
    handleSelectPlan,
    handleCancelCheckout,
  }
}

export default usePricingTable
